import { File, Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { useCarousel } from "../../hooks/useCarousel.js";
import { chevronLeft, chevronRight } from "../../icons/index.js";
import {
  ButtonAction,
  ButtonActionProps,
  ButtonActions,
} from "../../internal-components/Button.js";
import { VerticalSpacer } from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import type { BackgroundColor } from "../../types/theme.js";
import { Video } from "../Video/Video.js";
import {
  ArrowButton,
  StyledInfoGraphicConstrainer,
  StyledInfoGraphicDots,
  StyledInfoGraphicRichText,
  StyledInfoGraphicWrapper,
} from "./styles.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";

export interface InfoGraphicProps {
  width?: "medium" | "full";
  backgroundColor?: BackgroundColor;
  text?: string;
  actions?: ButtonActionProps[];

  graphics?: InfoGraphicItem[];
  graphicsPosition?: "left" | "right";
}

interface InfoGraphicItem {
  id: string;
  image?: Image;
  file?: File;
  poster?: Image;
}

export function InfoGraphic({
  width = "medium",
  backgroundColor = "surface1",
  graphicsPosition = "left",
  graphics = [],
  actions = [],
  text = "",
  ...props
}: InfoGraphicProps) {
  const { items, prev, next, dots, active, setSlice } = useCarousel(graphics, {
    limit: 1,
  });
  const activeGraphic = items[0];
  const showCarousel = graphics.length > 1;
  const hasGraphics = graphics.length > 0;

  return (
    <VerticalSpacer>
      <StyledInfoGraphicConstrainer
        $width={width}
        $bg={backgroundColor}
        $graphicsPosition={graphicsPosition}
      >
        <StyledInfoGraphicWrapper
          $width={width}
          $graphicsPosition={graphicsPosition}
        >
          {activeGraphic && (
            <>
              {showCarousel && (
                <ArrowButton onClick={prev} $position="left">{chevronLeft}</ArrowButton>
              )}
              {showCarousel && (
                <ArrowButton onClick={next} $position="right">{chevronRight}</ArrowButton>
              )}
              <Graphic key={activeGraphic.id} item={activeGraphic} {...props} />
              {showCarousel && (
                <StyledInfoGraphicDots
                  dots={dots}
                  active={active}
                  setSlice={setSlice}
                  showBorder
                />
              )}
            </>
          )}
        </StyledInfoGraphicWrapper>
        <StyledInfoGraphicRichText $hasGraphics={hasGraphics}>
          <RichText
            dangerouslySetInnerHTML={{ __html: text }}
            {...createEditableProps("html", "text")}
          />
          {actions.length > 0 && (
            <ButtonActions>
              {actions.map((action, i) => (
                <ButtonAction key={i} {...action} />
              ))}
            </ButtonActions>
          )}
        </StyledInfoGraphicRichText>
      </StyledInfoGraphicConstrainer>
    </VerticalSpacer>
  );
}

function Graphic({ item, ...props }: { item: InfoGraphicItem }) {
  if (item.file)
    return (
      <Video {...props} videos={[{ file: item.file }]} poster={item.poster ?? item.image} />
    );
  if (item.image) return <GraphicImage image={item.image} />;
  return <div />;
}

function GraphicImage({ image }: { image: Image }) {
  return (
    <img src={transformImageUrl(image, { width: 2400 })} alt={image.altText} />
  );
}
